import React, { useState, useMemo, useCallback, useEffect, Suspense } from "react";
import { createPortal } from "react-dom";
import { safeConsole } from "../../utils/browserCompatibility";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { parse, format } from "date-fns";
import { sanitizeText, sanitizeEmail, sanitizePhone, sanitizeNumber } from "../../utils/sanitization";
import { CheckCircle, AlertCircle, Info, ChevronDown, ChevronRight } from "lucide-react";
import { rideOptionsByPark } from "../../data/rideOptionsByPark";
import { filterAvailableRides, getSectionOptionAvailability, filterAvailableSectionOptions } from "../../utils/availabilityRules";
import OptimizedButton from "./UX/OptimizedButton";
import ResortSelector from './ResortSelector';
import RideSelector from './RideSelector';
import ShowSelector from './ShowSelector';
import RestaurantSelector from './RestaurantSelector';
import ExperienceSelector from './ExperienceSelector';
import { getCharacterOptionsForParksGrouped } from '../../data/characters.js';

// Helper function to handle Park Completionist selection (reserved for future use)
const _handleParkCompletionistSelection = (formData, handleMultiSelect) => {
  safeConsole.log('🎯 handleParkCompletionistSelection called with:', { formData, handleMultiSelect });
  const selectedParks = formData.parkVisitPlans?.parks || [];
  const allRideNames = selectedParks.flatMap((park) =>
    (rideOptionsByPark[park] || []).map((r) => r.name)
  );
  const { filtered: allRides } = filterAvailableRides(allRideNames, formData);
  safeConsole.log('🎯 All rides to select:', allRides);
  if (handleMultiSelect && allRides.length > 0) {
    handleMultiSelect('mustRides', allRides);
  }
};

const ModernFieldRenderer = ({
  field,
  formData,
  handleChange,
  handleMultiSelect,
  formMeta = {},
}) => {
  // State for Park Completionist modal
  const [showParkCompletionistModal, setShowParkCompletionistModal] = useState(false);
  const [pendingParkCompletionistSelection, setPendingParkCompletionistSelection] = useState(null);
  // State for character category dropdowns (which categories are expanded)
  const [expandedCharacterCategories, setExpandedCharacterCategories] = useState(() => new Set());
  // Phone number formatting function
  const formatPhoneNumber = (value) => {
    // Remove all non-digit characters
    const phoneNumber = value.replace(/\D/g, '');
    
    // Apply formatting based on length
    if (phoneNumber.length === 0) return '';
    if (phoneNumber.length <= 3) return `(${phoneNumber}`;
    if (phoneNumber.length <= 6) return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  };

  // Auto-capitalize function for name fields
  const capitalizeName = (value) => {
    return value
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Check if this is a phone number field
  const isPhoneField = field.id === 'phoneNumber' || field.label?.toLowerCase().includes('phone');
  // Memoized field mapping for better performance
  const fieldToSection = useMemo(() => ({
    // Basic Information
    fullName: 'basicInformation',
    email: 'basicInformation', 
    phoneNumber: 'basicInformation',
    startDate: 'basicInformation',
    endDate: 'basicInformation',
    
    // Group Information
    adults: 'groupInfo',
    numAdultMales: 'groupInfo',
    numAdultFemales: 'groupInfo',
    kids: 'groupInfo',
    numBoys: 'groupInfo',
    numGirls: 'groupInfo',
    agesOfBoys: 'groupInfo',
    agesOfGirls: 'groupInfo',
    
    // Ticket & Park Access
    ticketType: 'parkTicketAccess',
    hasParkHopper: 'parkTicketAccess',
    specialEvents: 'parkTicketAccess',
    
    // Park Selection
    parks: 'parkVisitPlans',
    mkDays: 'parkVisitPlans',
    hsDays: 'parkVisitPlans',
    epcotDays: 'parkVisitPlans',
    akDays: 'parkVisitPlans',
    tlDays: 'parkVisitPlans',
    bbDays: 'parkVisitPlans',
    includeRestDays: 'parkVisitPlans',
    restDayPlacement: 'parkVisitPlans',
    restDayStyle: 'parkVisitPlans',
    restDayCount: 'parkVisitPlans',
    finalParkPreference: 'parkVisitPlans',

    // Accommodation & Transportation
    transportation: 'accommodation',
    stayingOnProperty: 'accommodation',
    resort: 'accommodation',
    otherHotel: 'accommodation',
    
    // Attraction & Ride Preferences
    lightningLane: 'attractionRideStrategy',
    rideStrategy: 'attractionRideStrategy',
    mustRides: 'attractionRideStrategy',
    
    // Shows & Firework Preferences
    showStrategy: 'showFireworkStrategy',
    mustSeeShows: 'showFireworkStrategy',
    
    // Dining Preferences
    diningPlanInterest: 'diningPreferences',
    restaurantStrategy: 'diningPreferences',
    mustTryRestaurants: 'diningPreferences',
    diningTime: 'diningPreferences',
    allergies: 'diningPreferences',
    
    // Time & Energy Preferences
    resortBenefitsNote: 'timeEnergy',
    parkStartTime: 'timeEnergy',
    breakTime: 'timeEnergy',
    
    // Special Considerations
    celebration: 'specialConsiderations',
    celebrationDetails: 'specialConsiderations',
    celebrationDate: 'specialConsiderations',
    celebrationOtherDetails: 'specialConsiderations',
    hasDisability: 'specialConsiderations',
    disabilities: 'specialConsiderations',
    additionalInfo: 'specialConsiderations',
    additionalInfoDetails: 'specialConsiderations',
    
    // Character Meet & Greets
    interestedInCharacterMeetGreets: 'characterMeetGreets',
    mustMeetCharacters: 'characterMeetGreets',
    mustMeetCharactersOther: 'characterMeetGreets',
    
    // Specialty/Premium Experiences
    enchantedExperiences: 'specialtyExperiences',
    mustExperience: 'specialtyExperiences',
    
    // Fantasmic! Dining Package (Shows section)
    fantasmicDiningPackageInterest: 'showFireworkStrategy',
    fantasmicDiningPackageRestaurants: 'showFireworkStrategy',
  }), []);

  // Helper function to get value from nested form data structure
  const tripStartDate = formData?.basicInformation?.startDate ?? null;

  const hasOptionAvailabilityRules = useCallback((option) => {
    if (option == null || typeof option !== "object") return false;
    return (
      (Array.isArray(option.closureNoteKeywords) && option.closureNoteKeywords.length > 0) ||
      (Array.isArray(option.openingNoteKeywords) && option.openingNoteKeywords.length > 0) ||
      option.note != null ||
      option.disabled != null
    );
  }, []);

  const getOptionAvailabilityState = useCallback(
    (option) => {
      if (!hasOptionAvailabilityRules(option)) {
        return {
          available: option?.disabled !== true,
          reason: option?.tooltip ?? null,
        };
      }
      const result = getSectionOptionAvailability(option, tripStartDate);
      return { available: result.available, reason: result.reason ?? option?.tooltip ?? null };
    },
    [tripStartDate, hasOptionAvailabilityRules]
  );

  const getFieldValue = useCallback((fieldId) => {
    const section = fieldToSection[fieldId];

    if (section && formData[section]) {
      return formData[section][fieldId];
    }

    // Fallback to direct access
    return formData[fieldId];
  }, [formData, fieldToSection]);

  const value = getFieldValue(field.id);
  const isParkDayField = ['mkDays', 'hsDays', 'epcotDays', 'akDays', 'tlDays', 'bbDays'].includes(field.id);

  // Dynamic options for dropdowns
  const dynamicOptions = useMemo(() => {
    if (field.dynamicFrom) {
      const dynamicValue = getFieldValue(field.dynamicFrom);
      const count = parseInt(dynamicValue || "0", 10);
      
      if (field.id === "numAdultMales" || field.id === "numAdultFemales") {
        const adults = parseInt(getFieldValue('adults') || "0", 10);
        return Array.from({ length: adults + 1 }, (_, i) => `${i}`);
      }
      
      if (field.id === "numBoys" || field.id === "numGirls") {
        const kids = parseInt(getFieldValue('kids') || "0", 10);
        return Array.from({ length: kids + 1 }, (_, i) => `${i}`);
      }
      
      return Array.from({ length: count + 1 }, (_, i) => `${i}`);
    }
    
    return field.options || [];
  }, [field, getFieldValue]);

  // When trip start date or parks selection changes, remove any parks that became disabled (e.g. water parks by opening date)
  useEffect(() => {
    if (field.id !== "parks" || !field.options) return;
    const parks = getFieldValue("parks") || [];
    if (parks.length === 0) return;
    const { filtered, removed } = filterAvailableSectionOptions(parks, field.options, formData);
    if (filtered.length !== parks.length) {
      handleChange("parks", filtered);
      if (removed.includes("Typhoon Lagoon")) handleChange("tlDays", "");
      if (removed.includes("Blizzard Beach")) handleChange("bbDays", "");
    }
  }, [field.id, field.options, formData?.parkVisitPlans?.parks, formData?.basicInformation?.startDate, formData, getFieldValue, handleChange]);

  const renderField = () => {
    // Info field
    if (field.type === "info") {
      return (
        <div className="p-6 rounded-xl border shadow-sm backdrop-blur-md bg-white/95 border-white/20">
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Info className="flex-shrink-0 w-5 h-5 text-blue-600" />
              <h4 className="text-base font-semibold text-gray-900" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>Information</h4>
            </div>
            <div className="text-sm leading-relaxed text-gray-700 whitespace-pre-line" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
              {field.id === "resortsInfoNote" ? (
                <div>
                  <div className="mb-2 text-sm font-semibold text-gray-800">
                    Want quick and scenic access to EPCOT and Hollywood Studios?
                  </div>
                  <div className="mb-4">
                    Resorts with direct access to the Disney Skyliner include:
                    <br />- Disney's Riviera Resort (Deluxe)
                    <br />- Disney's Caribbean Beach Resort (Moderate)
                    <br />- Disney's Art of Animation Resort (Value)
                    <br />- Disney's Pop Century Resort (Value)
                  </div>
                  <div className="mt-6 mb-2 text-sm font-semibold text-gray-800">
                    Prefer convenient Monorail access to Magic Kingdom and EPCOT?
                  </div>
                  <div>
                    Monorail-connected resorts include:
                    <br />- Disney's Contemporary Resort (Deluxe)
                    <br />- Disney's Polynesian Village Resort (Deluxe)
                    <br />- Disney's Grand Floridian Resort & Spa (Deluxe)
                  </div>
                </div>
              ) : field.id === "parksInfoNote" ? (
                <div>
                  <div className="mb-2 text-sm font-semibold text-gray-800">
                    Did you know?
                  </div>
                  <div>
                    From May 26th through September 8th, 2026, Disney is offering a FREE Water Park ticket for the check-in day of your trip! If you're interested in using it, be sure to mention it to your agent
                    <br /><br />
                    Disney Springs is a fun destination for shopping, dining, and entertainment — and no ticket is required! Disney buses run regularly to and from Disney Springs from all resorts
                  </div>
                </div>
              ) : (
                field.label
              )}
            </div>
          </div>
        </div>
      );
    }

    // Resort Benefits Note field
    if (field.type === "resortBenefitsNote") {
      const selectedResort = getFieldValue('resort');
      
      if (!selectedResort) {
        return null; // Don't show if no resort selected
      }

      const resortLower = selectedResort.toLowerCase();
      
      // Check if it's a Deluxe Resort or Villa (gets extended evening hours)
      const isDeluxeProperty = resortLower.includes('deluxe resorts') ||
        resortLower.includes('deluxe villas') ||
        resortLower.includes('grand floridian') ||
        resortLower.includes('polynesian') ||
        resortLower.includes('contemporary') ||
        resortLower.includes('wilderness lodge') ||
        resortLower.includes('beach club') ||
        resortLower.includes('yacht club') ||
        resortLower.includes('boardwalk') ||
        resortLower.includes('animal kingdom lodge') ||
        resortLower.includes('villas') || 
        resortLower.includes('bay lake tower') ||
        resortLower.includes('beach club villas') ||
        resortLower.includes('boardwalk villas') ||
        resortLower.includes('kidani village') ||
        resortLower.includes('jambo house') ||
        resortLower.includes('copper creek') ||
        resortLower.includes('saratoga springs') ||
        resortLower.includes('old key west');

      return (
        <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 shadow-sm backdrop-blur-md">
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Info className="flex-shrink-0 w-5 h-5 text-blue-600" />
              <h4 className="text-base font-semibold text-blue-800" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
                Resort Benefits Available
              </h4>
            </div>
            <div className="space-y-2 text-sm leading-relaxed text-blue-700" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
              <p>
                <strong>Early Theme Park Entry:</strong> Enjoy 30-minute early entry to select attractions at Walt Disney World theme parks every day.
              </p>
              {isDeluxeProperty && (
                <p>
                  <strong>Extended Evening Hours:</strong> Take advantage of extended evening hours on select nights at select parks.
                </p>
              )}
              <p className="text-xs italic text-blue-600">
                * Please consult with your travel agent for date-specific park hours and availability.
              </p>
            </div>
          </div>
        </div>
      );
    }

    // Text, Email, Number inputs
    if (field.type === "text" || field.type === "email" || field.type === "number") {
      const isBasicInfoLocked = formMeta?.basicInfoLockedFromTrip === true &&
        ["fullName", "email", "phoneNumber"].includes(field.id);
      return (
        <div className="space-y-2">
          <label htmlFor={field.id} className="block mb-2 text-sm font-semibold leading-tight text-gray-800" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
            {field.label}
            {field.required && <span className="ml-1 text-red-500">*</span>}
          </label>
          
          <div className="relative">
            <input
              type={field.type === "number" ? "number" : field.type}
              id={field.id}
              value={isPhoneField ? formatPhoneNumber(value || "") : (value || "")}
              disabled={isBasicInfoLocked}
              onChange={(e) => {
                const rawValue = e.target.value;
                let processedValue;

                if (isPhoneField) {
                  processedValue = sanitizePhone(rawValue);
                  // Store digits-only for consistency with formatPhoneNumber display
                  processedValue = processedValue.replace(/\D/g, '');
                } else if (field.type === 'email' || field.id === 'email') {
                  processedValue = sanitizeEmail(rawValue);
                } else if (field.type === 'number') {
                  const num = sanitizeNumber(rawValue);
                  processedValue = num !== null ? String(num) : '';
                } else if (field.id === 'fullName') {
                  processedValue = capitalizeName(rawValue);
                } else {
                  processedValue = rawValue;
                }

                handleChange(field.id, processedValue);
              }}
              placeholder={field.placeholder || ""}
              maxLength={isPhoneField ? 14 : undefined}
              className={`block w-full p-3 text-sm font-medium bg-gray-700 text-white rounded-lg border border-gray-600 focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200 placeholder-gray-300 appearance-none ${
                isBasicInfoLocked ? 'opacity-90 cursor-not-allowed' : ''
              } ${field.required && !value ? 'border-red-500 focus:ring-red-500' : 
                value ? 'border-blue-300 focus:ring-blue-500' : ''
              }`}
              style={{
                fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif',
                fontSize: '14px',
                lineHeight: '1.5',
                height: '44px'
              }}
              aria-describedby={`${field.id}-help`}
              aria-invalid={field.required && !value}
              autoComplete="off"
            />
            
            {/* Validation Icons */}
            {field.required && !value && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <AlertCircle className="w-5 h-5 text-red-500" />
              </div>
            )}
            {value && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <CheckCircle className="w-5 h-5 text-blue-500" />
              </div>
            )}
          </div>
          
          {field.required && !value && (
            <p id={`${field.id}-help`} className="flex items-center space-x-1 text-sm text-red-400" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
              <AlertCircle className="w-4 h-4" />
              <span>This field is required</span>
            </p>
          )}
        </div>
      );
    }

    // Textarea
    if (field.type === "textarea") {
      return (
        <div className="space-y-2">
          <label htmlFor={field.id} className="block mb-2 text-sm font-semibold leading-tight text-gray-800" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
            {field.label}
            {field.required && <span className="ml-1 text-red-500">*</span>}
          </label>
          
          <textarea
            id={field.id}
            value={value || ""}
            onChange={(e) => handleChange(field.id, sanitizeText(e.target.value))}
            placeholder={field.placeholder || ""}
            rows={4}
            className="block px-4 py-3 w-full text-sm font-medium placeholder-gray-300 text-white bg-gray-700 rounded-lg border-2 border-gray-600 transition-all duration-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-500 resize-vertical"
            style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}
          />
        </div>
      );
    }

    // Dropdown
    if (field.type === "dropdown") {
      const isDisabled = isParkDayField &&
        !formMeta.hasFlexibleAccess &&
        formMeta.remainingParkDays <= 0 &&
        parseInt(value || "0", 10) === 0;

      // Special handling for specialEvents field with asterisk note
      const isSpecialEventsField = field.id === "specialEvents";
      let mainLabel = field.label;
      let asteriskNote = "";

      if (isSpecialEventsField && field.label.includes(" * ")) {
        const parts = field.label.split(" * ");
        mainLabel = parts[0];
        asteriskNote = parts[1];
      }

      return (
        <div className="space-y-2">
          <div>
            <label htmlFor={field.id} className="block mb-2 text-sm font-semibold leading-tight text-gray-800" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
              {mainLabel}
              {field.required && <span className="ml-1 text-red-500">*</span>}
            </label>
            {asteriskNote && (
              <p className="mb-2 text-sm italic text-blue-600" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
                * {asteriskNote.replace(/\.$/, '')}
              </p>
            )}
          </div>
          
          <div className="relative">
            <select
              id={field.id}
              value={String(value || "")}
              onChange={(e) => {
                const newVal = e.target.value;
                const opt = (dynamicOptions || []).find(
                  (o) => (typeof o === "object" ? o.value : o) === newVal
                );
                const { available } = getOptionAvailabilityState(opt ?? newVal);
                if (!available) return;
                handleChange(field.id, newVal);
              }}
              className={`w-full p-3 bg-gray-700 rounded-lg border border-gray-600 text-sm font-medium text-white transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary appearance-none cursor-pointer ${
                isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
              style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}
              disabled={isDisabled}
            >
              <option value="">Select an option</option>
              {(dynamicOptions || []).map((option) => {
                const optValue = typeof option === "object" ? option.value : option;
                const optLabel = typeof option === "object" ? option.label : option;
                const { available, reason } = getOptionAvailabilityState(option);
                return (
                  <option key={optValue} value={optValue} disabled={!available} title={!available ? (reason ?? undefined) : undefined}>
                    {optLabel}
                  </option>
                );
              })}
            </select>
            
            {/* Custom dropdown arrow */}
            <div className="flex absolute inset-y-0 right-0 items-center pr-3 pointer-events-none">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
            
            {/* Tooltip for disabled park day fields */}
            {isDisabled && (
              <div className="absolute left-0 -top-2 z-10 transform -translate-y-full">
                <div className="px-3 py-2 max-w-xs text-xs text-white bg-gray-800 rounded-lg border border-gray-600 shadow-lg" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
                  <div className="flex items-center space-x-1">
                    <AlertCircle className="flex-shrink-0 w-3 h-3 text-yellow-400" />
                    <span>You've exceeded your park days limit. Enable Park Hopping or reduce other park days to add more.</span>
                  </div>
                  {/* Arrow pointing down */}
                  <div className="absolute left-4 top-full w-0 h-0 border-t-4 border-r-4 border-l-4 border-transparent border-t-gray-800"></div>
                </div>
              </div>
            )}
          </div>
        </div>
      );
    }

    // Yes/No
    if (field.type === "yesno") {
      return (
        <div className="space-y-2">
          <label className="block mb-2 text-sm font-semibold leading-tight text-gray-800" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
            {field.label}
            {field.required && <span className="ml-1 text-red-500">*</span>}
          </label>
          
          <div className="flex gap-3" role="group">
            {["Yes", "No"].map((option) => (
              <button
                key={option}
                type="button"
                className={`px-4 py-2.5 text-sm font-semibold rounded-lg border transition-all duration-200 ${
                  value === option
                    ? "bg-blue-600 text-white border-blue-600 shadow-md"
                    : "bg-gray-700 text-white border-gray-600 hover:border-blue-400 hover:bg-gray-600"
                }`}
                style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}
                onClick={() => handleChange(field.id, option)}
                aria-pressed={value === option}
              >
                {option}
              </button>
            ))}
          </div>
        </div>
      );
    }

    // Multi-select (for parks, allergies, etc.)
    if (field.type === "multi") {
      return (
        <div className="space-y-2">
          <label className="block mb-2 text-sm font-semibold leading-tight text-gray-800" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
            {field.label}
            {field.required && <span className="ml-1 text-red-500">*</span>}
          </label>
          
          <div className="flex flex-wrap gap-2">
            {(field.options || []).map((option, index) => {
              const optionKey = typeof option === 'object' ? option.label || index : option;
              const optionText = typeof option === 'object' ? option.label : option;
              const optionValue = typeof option === 'object' ? option.value : option;
              const { available, reason } = getOptionAvailabilityState(option);
              const isDisabled = !available;
              const tooltip = reason ?? (typeof option === 'object' ? option.tooltip : null);
              
              return (
                <div key={optionKey} className="relative group">
                  <button
                    type="button"
                    className={`px-3 py-2 text-sm font-semibold rounded-lg border transition-all duration-200 ${
                      isDisabled
                        ? "bg-gray-400 text-gray-200 border-gray-400 cursor-not-allowed opacity-60"
                        : (value || []).includes(optionValue)
                        ? "bg-blue-600 text-white border-blue-600 shadow-md"
                        : "bg-gray-700 text-white border-gray-600 hover:border-blue-400 hover:bg-gray-600"
                    }`}
                    style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}
                    onClick={() => {
                      if (isDisabled) return;
                      const currentValues = value || [];
                      const updatedValues = currentValues.includes(optionValue)
                        ? currentValues.filter((v) => v !== optionValue)
                        : [...currentValues, optionValue];
                      handleChange(field.id, updatedValues);
                    }}
                    aria-pressed={(value || []).includes(optionValue)}
                    aria-disabled={isDisabled}
                    disabled={isDisabled}
                    title={tooltip || undefined}
                  >
                    {optionText}
                  </button>
                  
                  {isDisabled && tooltip && (
                    <div className="absolute bottom-full left-1/2 z-10 px-3 py-2 mb-2 w-64 text-xs text-white bg-gray-900 rounded-lg shadow-lg opacity-0 transition-opacity duration-200 transform -translate-x-1/2 pointer-events-none group-hover:opacity-100">
                      {tooltip}
                      <div className="absolute top-full left-1/2 w-0 h-0 border-t-4 border-r-4 border-l-4 border-transparent transform -translate-x-1/2 border-t-gray-900"></div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    // Single-select (for Yes/No questions like lightning lane, enchanted experiences)
    if (field.type === "single") {
      return (
        <div className="space-y-2">
          <label className="block mb-2 text-sm font-semibold leading-tight text-gray-800" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
            {field.label}
            {field.required && <span className="ml-1 text-red-500">*</span>}
          </label>
          
          <div className="flex flex-wrap gap-2">
            {(field.options || []).map((option, index) => {
              const optionKey = typeof option === 'object' ? option.label || index : option;
              const optionText = typeof option === 'object' ? option.label : option;
              const optionValue = typeof option === 'object' ? option.value : option;
              const { available, reason } = getOptionAvailabilityState(option);
              const isDisabled = !available;
              const isSelected = value === option || value === optionValue;
              
              return (
                <button
                  key={optionKey}
                  type="button"
                  className={`px-3 py-2 text-sm font-semibold rounded-lg border transition-all duration-200 ${
                    isDisabled
                      ? "bg-gray-400 text-gray-200 border-gray-400 cursor-not-allowed opacity-60"
                      : isSelected
                      ? "bg-blue-600 text-white border-blue-600 shadow-md"
                      : "bg-gray-700 text-white border-gray-600 hover:border-blue-400 hover:bg-gray-600"
                  }`}
                  style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}
                  onClick={() => {
                    if (isDisabled) return;
                    handleChange(field.id, optionValue);
                  }}
                  aria-pressed={isSelected}
                  aria-disabled={isDisabled}
                  disabled={isDisabled}
                  title={(reason ?? (typeof option === 'object' ? option.tooltip : null)) || undefined}
                >
                  {optionText}
                </button>
              );
            })}
          </div>
        </div>
      );
    }

    // Checkbox (for ride strategy, show strategy, etc.)
    if (field.type === "checkbox") {
      return (
        <div className="space-y-2">
          <label className="block mb-2 text-sm font-semibold leading-tight text-gray-800" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
            {field.label}
            {field.required && <span className="ml-1 text-red-500">*</span>}
          </label>
          
          <div className="space-y-3">
            {(field.options || []).map((option) => {
              const optionValue = typeof option === 'object' ? option.value : option;
              const optionLabel = typeof option === 'object' ? option.label : option;
              const isSelected = Array.isArray(value) && (value.includes(option) || value.includes(optionValue));
              const { available, reason } = getOptionAvailabilityState(option);
              const isDisabled = !available;
              const isParkCompletionist = (optionValue ?? option) === "Park Completionist – Ride everything possible";
              
              return (
                <label
                  key={optionValue ?? option}
                  className={`flex items-center space-x-3 ${isDisabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
                  title={isDisabled ? (reason ?? (typeof option === 'object' ? option.tooltip : null)) : undefined}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    disabled={isDisabled}
                    onChange={() => {
                      if (isDisabled) return;
                      const currentValues = value || [];
                      const updatedValues = isSelected
                        ? currentValues.filter((v) => v !== option && v !== optionValue)
                        : [...currentValues, optionValue ?? option];
                      handleChange(field.id, updatedValues);
                      
                      if (isParkCompletionist && !isSelected) {
                        setPendingParkCompletionistSelection({
                          fieldId: field.id,
                          currentValues: currentValues
                        });
                        setShowParkCompletionistModal(true);
                        window.dispatchEvent(new CustomEvent('parkCompletionistStart', {
                          detail: { timestamp: Date.now() }
                        }));
                      }
                    }}
                    className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 focus:ring-2"
                    aria-disabled={isDisabled}
                  />
                  <span className="text-sm text-gray-700" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>{optionLabel ?? option}</span>
                </label>
              );
            })}
          </div>
        </div>
      );
    }

    // Dynamic Resort Dropdown
    if (field.type === "dynamicResortDropdown") {
      const stayingOnProperty = getFieldValue('stayingOnProperty');
      
      if (!stayingOnProperty) {
        return (
          <div className="p-4 rounded-lg border backdrop-blur-md bg-white/95 border-white/20">
            <p className="text-sm text-gray-600" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
              Please answer the "Where will you be staying this trip?" question first.
            </p>
          </div>
        );
      }
      
      if (stayingOnProperty === "Off Disney Property") {
        return (
          <div className="p-4 rounded-lg border backdrop-blur-md bg-white/95 border-white/20">
            <p className="text-sm text-blue-700" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
              No resort selection needed — you've selected an off-property hotel.
            </p>
          </div>
        );
      }
      
      return (
        <div className="space-y-2">
          <Suspense fallback={<div className="text-sm text-gray-600" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>Loading resorts...</div>}>
            <ResortSelector
              selectedCategory={stayingOnProperty}
              selectedResort={value}
              onChange={handleChange}
            />
          </Suspense>
        </div>
      );
    }

    // Dynamic Ride Dropdown
    if (field.type === "dynamicRideDropdown") {
      const rideStrategy = getFieldValue('rideStrategy');
      const selectedParks = getFieldValue('parks') || [];
      
      if (!rideStrategy || rideStrategy.length === 0) {
        return (
          <div className="p-4 rounded-lg border backdrop-blur-md bg-white/95 border-white/20">
            <p className="text-sm text-gray-600" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
              Please answer the "Ride Strategy" question first to see available rides.
            </p>
          </div>
        );
      }
      
      if (!selectedParks || selectedParks.length === 0) {
        return (
          <div className="p-4 rounded-lg border backdrop-blur-md bg-white/95 border-white/20">
            <p className="text-sm text-gray-600" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
              Please go back and select at least one park in the "Park Selection" section to see available rides.
            </p>
          </div>
        );
      }
      
      return (
        <div className="space-y-2">
          <Suspense fallback={<div className="text-sm text-gray-600" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>Loading rides...</div>}>
            <RideSelector
              field={field}
              selectedParks={selectedParks}
              selected={value || []}
              formData={formData}
              showAllRides={getFieldValue('showAllRides')}
              setShowAllRides={(val) => handleChange("showAllRides", val)}
              handleChange={handleChange}
            />
          </Suspense>
        </div>
      );
    }

    // Dynamic Show Dropdown
    if (field.type === "dynamicShowDropdown") {
      const showStrategy = getFieldValue('showStrategy');
      const selectedParks = getFieldValue('parks') || [];
      
      if (showStrategy?.includes("Not Interested")) {
        return (
          <div className="p-4 rounded-lg border backdrop-blur-md bg-white/95 border-white/20">
            <p className="text-sm text-blue-700" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
              You selected "Not Interested" — skipping show selection.
            </p>
          </div>
        );
      }
      
      if (!showStrategy || showStrategy.length === 0) {
        return (
          <div className="p-4 rounded-lg border backdrop-blur-md bg-white/95 border-white/20">
            <p className="text-sm text-gray-600" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
              Please answer the "Show & Entertainment Interests" question first to see available shows.
            </p>
          </div>
        );
      }
      
      return (
        <div className="space-y-2">
          <Suspense fallback={<div className="text-sm text-gray-600" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>Loading shows...</div>}>
            <ShowSelector
              field={field}
              selectedParks={selectedParks}
              selected={value || []}
              formData={formData}
              showAllShows={getFieldValue('showAllShows')}
              setShowAllShows={(val) => handleChange("showAllShows", val)}
              handleChange={handleChange}
            />
          </Suspense>
        </div>
      );
    }

    // Dynamic Experience Dropdown
    if (field.type === "dynamicExperienceDropdown") {
      const enchantedExperiences = getFieldValue('enchantedExperiences');
      const selectedParks = getFieldValue('parks') || [];
      
      if (!enchantedExperiences || !enchantedExperiences.includes("Yes")) {
        return (
          <div className="p-4 rounded-lg border backdrop-blur-md bg-white/95 border-white/20">
            <p className="text-sm text-blue-700" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
              Please select "Yes" above to see premium experiences.
            </p>
          </div>
        );
      }
      
      return (
        <div className="space-y-2">
          <Suspense fallback={<div className="text-sm text-gray-600" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>Loading experiences...</div>}>
            <ExperienceSelector
              field={field}
              selectedParks={selectedParks}
              selected={value || []}
              formData={formData}
              handleChange={handleChange}
            />
          </Suspense>
        </div>
      );
    }

    // Dynamic Restaurant Dropdown
    if (field.type === "dynamicRestaurantDropdown") {
      const restaurantStrategy = getFieldValue('restaurantStrategy');
      
      if (!restaurantStrategy || restaurantStrategy.length === 0) {
        return (
          <div className="p-4 rounded-lg border backdrop-blur-md bg-white/95 border-white/20">
            <p className="text-sm text-gray-600" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
              Please answer the "Restaurant Preferences" question first to see available restaurants.
            </p>
          </div>
        );
      }
      
      return (
        <div className="space-y-2">
          <Suspense fallback={<div className="text-sm text-gray-600" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>Loading restaurants...</div>}>
            <RestaurantSelector
              field={field}
              selectedParks={getFieldValue('parks') || []}
              selected={value || []}
              formData={formData}
              showAllRestaurants={getFieldValue('showAllRestaurants')}
              setShowAllRestaurants={(val) => handleChange("showAllRestaurants", val)}
              handleChange={handleChange}
            />
          </Suspense>
        </div>
      );
    }

    // Date Picker
    if (field.type === "customDate") {
      // Browser detection for date picker compatibility
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      const isChrome = /chrome/i.test(navigator.userAgent);
      
      // Get start date for end date field to set proper minDate
      const startDate = field.id === 'endDate' ? getFieldValue('startDate') : null;
      const minDate = startDate ? parse(startDate, "yyyy-MM-dd", new Date()) : new Date();

      // For celebrationDate: default to trip month when no value selected
      const tripStartStr = getFieldValue('startDate');
      const openToDate = !value && field.id === 'celebrationDate' && tripStartStr
        ? (() => {
            const d = parse(tripStartStr, "yyyy-MM-dd", new Date());
            return isNaN(d.getTime()) ? undefined : d;
          })()
        : field.id === 'endDate' && startDate
          ? (() => {
              const d = parse(startDate, "yyyy-MM-dd", new Date());
              return isNaN(d.getTime()) ? undefined : d;
            })()
          : undefined;
      
      return (
        <div className="space-y-2">
          <label htmlFor={field.id} className="block mb-2 text-sm font-semibold leading-tight text-gray-800" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
            {field.label}
            {field.required && <span className="ml-1 text-red-500">*</span>}
          </label>
          
          <DatePicker
            selected={value ? (() => {
              try {
                const parsedDate = parse(value, "yyyy-MM-dd", new Date());
                return isNaN(parsedDate.getTime()) ? null : parsedDate;
              } catch {
                return null;
              }
            })() : null}
            onChange={(date) => {
              const formattedDate = date ? format(date, "yyyy-MM-dd") : "";
              handleChange(field.id, formattedDate);
            }}
            dateFormat="MM/dd/yyyy"
            minDate={minDate}
            placeholderText="MM/DD/YYYY"
            className="block px-4 py-3 w-full text-sm font-medium text-white bg-gray-700 rounded-lg border-2 border-gray-600 transition-all duration-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-500"
            style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}
            autoComplete="off"
            showPopperArrow={false}
            fixedHeight
            {...(openToDate && { openToDate })}
            // Browser-specific props for better compatibility
            {...(isSafari && {
              shouldCloseOnSelect: true,
              showMonthDropdown: false,
              showYearDropdown: false,
              dropdownMode: "select"
            })}
            {...(isChrome && {
              shouldCloseOnSelect: false,
              showMonthDropdown: false,
              showYearDropdown: false,
              // Force calendar to stay open for better UX
              onCalendarOpen: () => {
                // Ensure calendar opens to the correct month
                if (field.id === 'endDate' && startDate) {
                  const startDateObj = parse(startDate, "yyyy-MM-dd", new Date());
                  if (!isNaN(startDateObj.getTime())) {
                    // This will be handled by the minDate prop
                  }
                }
              }
            })}
          />
        </div>
      );
    }

    // Multiselect (e.g. mustMeetCharacters – park-filtered character list)
    // Handles both "multiselect" and "multiSelect" for mustMeetCharacters so the field always renders
    const isParkFilteredMultiselect =
      field.type === "multiselect" || (field.type === "multiSelect" && field.id === "mustMeetCharacters");
    if (isParkFilteredMultiselect) {
      const parksValue = getFieldValue("parks") || [];
      const themeParks = Array.isArray(parksValue)
        ? parksValue.filter((p) => p !== "Disney Springs")
        : [];
      const selected = Array.isArray(value) ? value : [];

      // mustMeetCharacters: grouped by category with checkbox dropdowns
      if (field.id === "mustMeetCharacters") {
        const groupedOptions = themeParks.length ? getCharacterOptionsForParksGrouped(themeParks) : {};
        const categoryKeys = Object.keys(groupedOptions);
        const totalOptions = categoryKeys.reduce((sum, k) => sum + (groupedOptions[k]?.length || 0), 0);

        if (totalOptions === 0) {
          return (
            <div className="space-y-2">
              <label className="block mb-2 text-sm font-semibold leading-tight text-gray-800" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
                {field.label}
                {field.required && <span className="ml-1 text-red-500">*</span>}
              </label>
              <p className="text-sm italic text-gray-500" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
                Select parks above to see characters available at your parks.
              </p>
            </div>
          );
        }

        const toggleCategory = (categoryKey) => {
          setExpandedCharacterCategories((prev) => {
            const next = new Set(prev);
            if (next.has(categoryKey)) next.delete(categoryKey);
            else next.add(categoryKey);
            return next;
          });
        };

        return (
          <div className="space-y-2">
            <label className="block mb-2 text-sm font-semibold leading-tight text-gray-800" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
              {field.label}
              {field.required && <span className="ml-1 text-red-500">*</span>}
            </label>
            <div
              className="rounded-xl border-2 border-gray-200 bg-white/95 shadow-sm overflow-hidden"
              role="group"
              aria-label={field.label}
            >
              {categoryKeys.map((categoryKey) => {
                const characters = groupedOptions[categoryKey] || [];
                const isExpanded = expandedCharacterCategories.has(categoryKey);
                const selectedInCategory = characters.filter((c) => selected.includes(c));
                return (
                  <div key={categoryKey} className="border-b border-gray-200 last:border-b-0">
                    <button
                      type="button"
                      onClick={() => toggleCategory(categoryKey)}
                      className="flex justify-between items-center w-full px-4 py-3 text-left text-sm font-medium text-gray-800 bg-gray-50 hover:bg-gray-100 transition-colors"
                      style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}
                      aria-expanded={isExpanded}
                    >
                      <span>{categoryKey}</span>
                      <span className="flex items-center gap-2">
                        {selectedInCategory.length > 0 && (
                          <span className="px-2 py-0.5 text-xs font-medium text-blue-700 bg-blue-100 rounded-full">
                            {selectedInCategory.length} selected
                          </span>
                        )}
                        {isExpanded ? (
                          <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-gray-500 flex-shrink-0" />
                        )}
                      </span>
                    </button>
                    {isExpanded && (
                      <div className="px-4 py-3 bg-white border-t border-gray-100 space-y-2 max-h-48 overflow-y-auto">
                        {characters.map((charName) => {
                          const isChecked = selected.includes(charName);
                          return (
                            <label
                              key={charName}
                              className="flex items-center gap-3 py-1.5 cursor-pointer rounded-md hover:bg-gray-50 px-2 -mx-2"
                              style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}
                            >
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => {
                                  const next = isChecked
                                    ? selected.filter((item) => item !== charName)
                                    : [...selected, charName];
                                  handleMultiSelect(field.id, next);
                                }}
                                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                aria-label={`Select ${charName}`}
                              />
                              <span className="text-sm text-gray-800">{charName}</span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="text-xs italic text-gray-500" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
              Expand a category and check the characters you&apos;d like to meet. You can select from multiple categories.
            </p>
            {selected.includes("Other") && (
              <div className="pt-2 mt-2 border-t border-gray-200">
                <label htmlFor="mustMeetCharactersOther" className="block mb-1.5 text-sm font-medium text-gray-800" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
                  Please specify which character(s) you&apos;d like to meet:
                </label>
                <input
                  id="mustMeetCharactersOther"
                  type="text"
                  value={getFieldValue("mustMeetCharactersOther") || ""}
                  onChange={(e) => handleChange("mustMeetCharactersOther", e.target.value)}
                  placeholder="e.g., Cinderella, Gaston, etc."
                  className="w-full px-3 py-2 text-sm font-medium text-gray-800 bg-white rounded-lg border-2 border-gray-300 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}
                  aria-label="Specify which character(s) you would like to meet"
                />
              </div>
            )}
          </div>
        );
      }

      // Other park-filtered multiselect: flat button list
      const options = field.options || [];
      if (options.length === 0) {
        return (
          <div className="space-y-2">
            <label className="block mb-2 text-sm font-semibold leading-tight text-gray-800" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
              {field.label}
              {field.required && <span className="ml-1 text-red-500">*</span>}
            </label>
            <p className="text-sm italic text-gray-500" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
              No options available.
            </p>
          </div>
        );
      }

      return (
        <div className="space-y-2">
          <label className="block mb-2 text-sm font-semibold leading-tight text-gray-800" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
            {field.label}
            {field.required && <span className="ml-1 text-red-500">*</span>}
          </label>
          <div
            className="flex flex-wrap gap-2 rounded-xl border-2 border-gray-200 bg-white/95 p-3 shadow-sm transition-all duration-200 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/20"
            role="group"
            aria-label={field.label}
          >
            {options.map((opt) => {
              const isSelected = selected.includes(opt);
              return (
                <button
                  key={opt}
                  type="button"
                  onClick={() => {
                    const next = isSelected
                      ? selected.filter((item) => item !== opt)
                      : [...selected, opt];
                    handleMultiSelect(field.id, next);
                  }}
                  className={`px-3 py-2 text-sm font-medium rounded-lg border transition-all duration-200 flex-shrink-0 ${
                    isSelected
                      ? "bg-blue-600 text-white border-blue-600 shadow-md"
                      : "bg-gray-700 text-white border-gray-600 hover:border-blue-400 hover:bg-gray-600"
                  }`}
                  style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}
                  aria-pressed={isSelected}
                >
                  {opt}
                </button>
              );
            })}
          </div>
          <p className="text-xs italic text-gray-500" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
            Tap or click to select more than one.
          </p>
        </div>
      );
    }

    // Multi-select (button-style toggles for field.options)
    if (field.type === "multiSelect") {
      return (
        <div className="space-y-2">
          <label className="block mb-2 text-sm font-semibold leading-tight text-gray-800" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
            {field.label}
            {field.required && <span className="ml-1 text-red-500">*</span>}
          </label>
          
          <div className="flex flex-wrap gap-2" role="group">
            {(field.options || []).map((option, index) => {
              // Handle both string and object options
              const optionKey = typeof option === 'object' ? option.label || index : option;
              const optionText = typeof option === 'object' ? option.label : option;
              
              return (
                <button
                  key={optionKey}
                  type="button"
                  onClick={() => {
                    const currentValue = value || [];
                    const newValue = currentValue.includes(option)
                      ? currentValue.filter((item) => item !== option)
                      : [...currentValue, option];
                    handleMultiSelect(field.id, newValue);
                  }}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md border transition-all duration-200 flex-shrink-0 ${
                    (value || []).includes(option)
                      ? "bg-blue-600 text-white border-blue-600 shadow-md"
                      : "bg-gray-700 text-white border-gray-600 hover:border-blue-400 hover:bg-gray-600"
                  }`}
                  style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}
                  aria-pressed={(value || []).includes(option)}
                >
                  {optionText}
                </button>
              );
            })}
          </div>
        </div>
      );
    }

    // Special selectors
    if (field.type === "rideSelector") {
      return (
        <RideSelector
          selectedRides={value || []}
          onSelectionChange={(rides) => handleMultiSelect(field.id, rides)}
          selectedParks={getFieldValue('parks') || []}
        />
      );
    }

    if (field.type === "showSelector") {
      return (
        <ShowSelector
          selectedShows={value || []}
          onSelectionChange={(shows) => handleMultiSelect(field.id, shows)}
          selectedParks={getFieldValue('parks') || []}
        />
      );
    }

    if (field.type === "resortSelector") {
      return (
        <ResortSelector
          selectedResort={value || ""}
          onSelectionChange={(resort) => handleChange(field.id, resort)}
        />
      );
    }

    if (field.type === "restaurantSelector") {
      return (
        <RestaurantSelector
          selectedRestaurants={value || []}
          onSelectionChange={(restaurants) => handleMultiSelect(field.id, restaurants)}
          selectedParks={getFieldValue('parks') || []}
        />
      );
    }

    if (field.type === "dynamic" && field.component === "ExperienceSelector") {
      return (
        <Suspense fallback={<div className="text-sm text-gray-600" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>Loading experiences...</div>}>
          <ExperienceSelector
            formData={formData}
            onChange={(fieldId, newValue) => handleMultiSelect(fieldId, newValue)}
            current={value || []}
          />
        </Suspense>
      );
    }

    // Default fallback
    return (
      <div className="space-y-2">
        <label className="block mb-2 text-sm font-semibold leading-tight text-gray-800" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
          {field.label}
          {field.required && <span className="ml-1 text-red-500">*</span>}
        </label>
        
        <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
          <p className="text-sm text-yellow-800" style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}>
            Field type "{field.type}" is not yet implemented in the modern renderer.
          </p>
        </div>
      </div>
    );
  };

  // Modal handlers
  const handleParkCompletionistConfirm = () => {
    safeConsole.log('🎯 Park Completionist Confirm clicked');
    if (pendingParkCompletionistSelection) {
      // Auto-select all rides for selected parks
      safeConsole.log('🎯 Calling handleParkCompletionistSelection');
      
  const selectedParks = formData.parkVisitPlans?.parks || [];
  const allRideNames = selectedParks.flatMap((park) =>
    (rideOptionsByPark[park] || []).map((r) => r.name)
  );
  const { filtered: allRides } = filterAvailableRides(allRideNames, formData);
      
      safeConsole.log('🎯 About to select rides:', allRides);
      
      // Call handleMultiSelect with the rides
      safeConsole.log('🎯 Calling handleMultiSelect with fieldId: mustRides, rides:', allRides);
      handleMultiSelect('mustRides', allRides);
      safeConsole.log('🎯 handleMultiSelect call completed');
      
      // Mark that auto-selection is complete with the actual rides data
      window.dispatchEvent(new CustomEvent('parkCompletionistComplete', {
        detail: { 
          timestamp: Date.now(),
          selectedRides: allRides,
          selectedParks: selectedParks
        }
      }));
      
      // Automatically trigger the next section flow for Park Completionist
      // This will show the "Review Your Ride Selections" modal directly
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('parkCompletionistAutoNext', {
          detail: { 
            timestamp: Date.now(),
            sectionId: 'attractionRideStrategy'
          }
        }));
      }, 200); // Small delay to ensure state is updated
    }
    setShowParkCompletionistModal(false);
    setPendingParkCompletionistSelection(null);
  };

  const handleParkCompletionistCancel = () => {
    if (pendingParkCompletionistSelection) {
      // User cancelled, remove the selection
      handleChange(pendingParkCompletionistSelection.fieldId, pendingParkCompletionistSelection.currentValues);
    }
    setShowParkCompletionistModal(false);
    setPendingParkCompletionistSelection(null);
  };

  return (
    <div className="space-y-4">
      {renderField()}
      
      {/* Park Completionist Confirmation Modal - Cross-browser & Mobile Compatible */}
      {showParkCompletionistModal && (typeof document !== 'undefined' && document.body ? createPortal(
        <div 
          className="flex fixed inset-0 z-[9999] justify-center items-center p-2 sm:p-4 bg-black bg-opacity-50"
          style={{ 
            // Ensure proper positioning on all devices
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            // Prevent scrolling on mobile when modal is open
            overflow: 'hidden',
            // Touch-friendly backdrop
            touchAction: 'none'
          }}
          onClick={(e) => {
            // Close modal when clicking backdrop (mobile-friendly)
            if (e.target === e.currentTarget) {
              handleParkCompletionistCancel();
            }
          }}
        >
          <div 
            className="p-4 mx-2 w-full max-w-sm bg-white rounded-lg border border-gray-300 shadow-xl sm:p-6 sm:max-w-md sm:rounded-xl sm:mx-4"
            style={{
              // Ensure modal is scrollable on small screens
              maxHeight: '90vh',
              overflowY: 'auto',
              // Prevent modal from being too wide on mobile
              minWidth: '280px',
              // Smooth animations
              animation: 'modalSlideIn 0.3s ease-out'
            }}
          >
            <div className="space-y-3 text-center sm:space-y-4">
              <h2 className="text-base font-bold text-blue-600 sm:text-lg">
                Park Completionist Selected!
              </h2>
              <p className="text-xs leading-relaxed text-gray-700 sm:text-sm">
                By selecting this option, <strong>ALL rides</strong> from your selected parks will be automatically added to your must-ride list.
              </p>
              <p className="text-xs leading-relaxed text-gray-700 sm:text-sm">
                This ensures you experience everything possible during your visit!
              </p>
              <div className="flex flex-col gap-2 justify-center mt-4 sm:gap-3 sm:mt-6">
                <OptimizedButton
                  onClick={handleParkCompletionistConfirm}
                  variant="primary"
                  className="w-full sm:w-auto min-h-[44px] text-sm sm:text-base"
                >
                  Continue
                </OptimizedButton>
                <OptimizedButton
                  onClick={handleParkCompletionistCancel}
                  variant="secondary"
                  className="w-full sm:w-auto min-h-[44px] text-sm sm:text-base"
                >
                  Go Back
                </OptimizedButton>
              </div>
            </div>
          </div>
        </div>,
        document.body
      ) : (
        // Fallback for browsers without portal support or SSR
        <div 
          className="flex fixed inset-0 z-[9999] justify-center items-center p-2 sm:p-4 bg-black bg-opacity-50"
          style={{ 
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            overflow: 'hidden',
            touchAction: 'none'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              handleParkCompletionistCancel();
            }
          }}
        >
          <div 
            className="p-4 mx-2 w-full max-w-sm bg-white rounded-lg border border-gray-300 shadow-xl sm:p-6 sm:max-w-md sm:rounded-xl sm:mx-4"
            style={{
              maxHeight: '90vh',
              overflowY: 'auto',
              minWidth: '280px',
              animation: 'modalSlideIn 0.3s ease-out'
            }}
          >
            <div className="space-y-3 text-center sm:space-y-4">
              <h2 className="text-base font-bold text-blue-600 sm:text-lg">
                Park Completionist Selected!
              </h2>
              <p className="text-xs leading-relaxed text-gray-700 sm:text-sm">
                By selecting this option, <strong>ALL rides</strong> from your selected parks will be automatically added to your must-ride list.
              </p>
              <p className="text-xs leading-relaxed text-gray-700 sm:text-sm">
                This ensures you experience everything possible during your visit!
              </p>
              <div className="flex flex-col gap-2 justify-center mt-4 sm:gap-3 sm:mt-6">
                <OptimizedButton
                  onClick={handleParkCompletionistConfirm}
                  variant="primary"
                  className="w-full sm:w-auto min-h-[44px] text-sm sm:text-base"
                >
                  Continue
                </OptimizedButton>
                <OptimizedButton
                  onClick={handleParkCompletionistCancel}
                  variant="secondary"
                  className="w-full sm:w-auto min-h-[44px] text-sm sm:text-base"
                >
                  Go Back
                </OptimizedButton>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ModernFieldRenderer;
